import { ConfirmModal, Loading, Table, TableRowMenuCell } from '@/components';
import { useGlobalTagPermission } from '@/features/authentication/_hooks/permission';
import { type TagBaseModel, TagTypeLabelModifier } from '@/features/tag/models';
import { useMessage } from '@/providers';
import {
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  datetimeFormat,
} from '@/utils';
import dayjs from 'dayjs';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useCreateGlobalTag,
  useDeleteGlobalTag,
  useGlobalTags,
  useUpdateGlobalTag,
} from '../hooks';
import { useDrawerState } from './DrawerStateProvider';
import { GlobalTagFormDrawer } from './GlobalTagFormDrawer';

export const GlobalTagsPage: FC = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const globalTags = useGlobalTags();
  const editPermission = useGlobalTagPermission(true);
  const { drawerState, setDrawerState } = useDrawerState();
  const { mutateAsync: createGlobalTagAsync } = useCreateGlobalTag();
  const { mutateAsync: updateGlobalTagAsync } = useUpdateGlobalTag();
  const { mutateAsync: deleteGlobalTagAsync } = useDeleteGlobalTag();
  const [activeModalType, setActiveModalType] = useState<'delete' | null>(null);
  const [selectedGlobalTag, setSelectedGlobalTag] = useState<z.infer<
    typeof TagBaseModel
  > | null>(null);

  if (globalTags.isPending || !globalTags.data) {
    return <Loading />;
  }

  const isDrawerOpen = drawerState.open && drawerState.type === 'global-tag';

  const selectedTag =
    isDrawerOpen && drawerState.isEditing
      ? globalTags.data.find((d) => d.id === drawerState.id)
      : undefined;

  const handleCloseDrawer = (): void => {
    setDrawerState({ open: false });
  };

  return (
    <>
      <Table
        dataSource={globalTags.data}
        rowKey="id"
        columns={[
          {
            title: t('scope-tag-type', { scope: t('global-tag') }),
            key: 'type',
            dataIndex: 'type',
            width: 250,
            render: (value) => TagTypeLabelModifier[value],
          },
          {
            title: t('scope-tag-name', { scope: t('global-tag') }),
            key: 'name',
            dataIndex: 'name',
          },
          {
            title: t('start-fiscal-year'),
            key: 'startYear',
            dataIndex: 'startYear',
            width: 200,
          },
          {
            title: t('end-fiscal-year'),
            key: 'endYear',
            dataIndex: 'endYear',
            width: 200,
          },
          {
            title: t('created-datetime'),
            key: 'createdAt',
            dataIndex: 'createdAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          {
            title: t('updated-datetime'),
            key: 'updatedAt',
            dataIndex: 'updatedAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          ...(editPermission.data
            ? [
                {
                  title: '',
                  key: 'actions',
                  fixed: 'right' as const,
                  width: TABLE_ROW_MENU_WIDTH,
                  onCell: () => ({
                    style: { minWidth: TABLE_ROW_MENU_WIDTH },
                  }),
                  render: (_, record) => (
                    <TableRowMenuCell
                      onEdit={() => {
                        setDrawerState({
                          open: true,
                          type: 'global-tag',
                          isEditing: true,
                          id: record.id,
                        });
                      }}
                      items={[
                        {
                          key: 'delete',
                          label: t('delete'),
                          danger: true,
                          onClick: () => {
                            setSelectedGlobalTag(record);
                            setActiveModalType('delete');
                          },
                        },
                      ]}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />

      <ConfirmModal
        open={activeModalType === 'delete' && selectedGlobalTag !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-item-confirmation', {
          item: selectedGlobalTag?.name ?? '',
        })}
        onOk={async () => {
          invariant(selectedGlobalTag, t('unreachable-code'));

          try {
            await deleteGlobalTagAsync(selectedGlobalTag.id);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setActiveModalType(null);
                setSelectedGlobalTag(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => {
          setActiveModalType(null);
          setSelectedGlobalTag(null);
        }}
        skipCheck
      />

      <GlobalTagFormDrawer
        open={isDrawerOpen}
        id={selectedTag?.id}
        onClose={handleCloseDrawer}
        type={
          drawerState.open &&
          drawerState.type === 'global-tag' &&
          drawerState.isEditing
            ? 'edit'
            : 'new'
        }
        initialValues={{
          ...selectedTag,
          period: [
            selectedTag?.startYear
              ? dayjs().year(selectedTag.startYear)
              : undefined,
            selectedTag?.endYear
              ? dayjs().year(selectedTag.endYear)
              : undefined,
          ],
        }}
        onSubmit={async (values) => {
          invariant(isDrawerOpen, t('unreachable-code'));

          drawerState.isEditing
            ? await updateGlobalTagAsync({ ...values, id: drawerState.id })
            : await createGlobalTagAsync({ ...values });
          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            (async () => {
              handleCloseDrawer();
            })(),
          ]);
        }}
      />
    </>
  );
};
